import React, { Fragment, FunctionComponent, memo, useEffect, useRef, useState } from 'react'
import styles from './ProjectList.module.sass'
import { isEmpty, isNonEmpty } from 'fp-ts/es6/Array'
import ProjectFilters from './ProjectFilters/ProjectFilters'
import { User } from '../../../../responses/users'
import SidePanel from '../../../etc/SidePanel/SidePanel'
import { LoadingSkeleton } from '../../../etc/loading-components'
import { RequestStatus } from '../../../../responses/response'
import { statusContext } from '../../../../constants/contexts'
import Helmet from 'react-helmet'
import { projectActions } from '../../../../redux/data/project/project-actions'
import TopBarProgress from 'react-topbar-progress-indicator'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import { useNavigation, useRootDispatch, useRootSelector, useAnyPermissions } from '../../../../utils/hooks'
import { AnimatePresence } from 'framer-motion'
import { userRenderer } from '../../../../utils/renderers'
import { isDefinedRecord, isDefinedTuple, pick } from '../../../../utils/utils'
import { shallowEqual } from 'react-redux'
import { PERMISSIONS } from '../../../../constants/permissions'
import { NavLink } from 'react-router-dom'
import { ROUTES } from '../../../../http/routes'
import Button, { ButtonStyle } from '../../../controls/Button/Button'
import PlusIcon from '../../../../../files/plus.svg'
import { flow } from 'fp-ts/function'
import { projectListActions } from '../../../../redux/pages/project-list'
import ProjectLine from './ProjectLine/ProjectLine'
import { Colors } from '../../../../styles'
import FolderOpenIcon from '../../../../../files/folder-open.svg'
import FolderIcon from '../../../../../files/folder.svg'
import ObjectList from './ObjectList'
import { eqId } from '../../../../utils/entity-utils'
import * as A from 'fp-ts/Array'
import { HasAllPermissions } from '../../../etc/HasPermission'
import { listStatusFilters } from './config'
import NotFound from '../../../etc/NotFound/NotFound'

const ProjectList: FunctionComponent = () => {
    const [users, setUsers] = useState<User[]>([])
    const dispatch = useRootDispatch()
    const {projects, status, filters, total, openProjects} = useRootSelector(state => pick(state.pages.projectList, 'projects', 'status', 'filters', 'total', 'openProjects'), shallowEqual)

    const openAll = openProjects.length === projects.length && projects.length > 0

    useEffect(() => {
        dispatch(projectActions.list.init(filters))
    }, [dispatch, filters])

    const listRef = useRef(null)
    useNavigation(listRef)

    const isFiltersActive = isDefinedRecord(filters.dropdowns) ||
                            isDefinedTuple(filters.openedAt) ||
                            (filters.search && filters.search != '') ||
                            (filters.tags && filters.tags.length != 0) ||
                            //isDefinedRecord(filters.taskStatus) ||
                            //(filters.object_design_status && filters.object_design_status != null) ||
                            //(filters.object_production_status && filters.object_production_status != null) ||
                            (filters.projectStatus && filters.projectStatus.length < 6)
    const isPermit = useAnyPermissions([PERMISSIONS.PROJECT.OPEN])
    return <div className={styles.projectList}>
        {
            status === RequestStatus.FETCHING && <TopBarProgress/>
        }
        <Helmet>
            <title>Список проектов</title>
        </Helmet>
        <statusContext.Provider value={isNonEmpty(projects) ? RequestStatus.SUCCESS : status}>
            <ProjectFilters
                value={filters}
                total={total}
                statusFilters={listStatusFilters}
                onChange={flow(projectListActions.updateFilters, dispatch)}
                onClear={() => dispatch(projectListActions.clearFilters())}
                toggle={<Button
                    title={openAll ? 'Свернуть все проекты' : 'Развернуть все проекты'}
                    buttonStyle={ButtonStyle.TINTED}
                    active={openAll}
                    onClick={() => dispatch(projectListActions.openProjects.toggleAll())}
                >
                    {openAll ? <FolderOpenIcon/> : <FolderIcon/>}
                </Button>}
                actions={<HasAllPermissions permissions={[PERMISSIONS.PROJECT.OPEN]}>
                    <NavLink
                        className={styles.openButton}
                        to={ROUTES.PROJECTS_NEW}
                    >
                        <Button
                            color={Colors.blue}
                            buttonStyle={ButtonStyle.BORDERED}
                        >
                            <PlusIcon/> Открыть проект
                        </Button>
                    </NavLink>
                </HasAllPermissions>}
            />
            <InfiniteScroll
                ref={listRef}
                className={styles.listContainer}
                onReach={() => dispatch(projectActions.list.more())}
            >
                <LoadingSkeleton className={styles.table} count={8} height={64}>
                    {
                        isEmpty(projects) ?
                            isFiltersActive ?
                                <NotFound
                                    headerText='Проекты не найдены.'
                                    infoText='Измените параметры фильтров вверху страницы.'
                                    imageName='EMPTY_1'
                                /> :
                                isPermit ?
                                    <NotFound
                                        headerText='Вы пока не создали проекты.'
                                        imageName='EMPTY_4'
                                        actions={<NavLink
                                            className={styles.openButton}
                                            to={ROUTES.PROJECTS_NEW}
                                        >
                                            <Button
                                                color={Colors.blue}
                                                buttonStyle={ButtonStyle.WHITE_BORDERED}
                                            >
                                                <PlusIcon/> Открыть проект
                                            </Button>
                                        </NavLink>}
                                    /> :
                                    <NotFound
                                        headerText='Пока нет проектов в которых вы участвуете.'
                                        infoText='Обратитесь к руководителю, чтобы он
                                        включил вас в проекты.'
                                        imageName='EMPTY_4'
                                    />
                        : ''
                    }
                    {
                        projects.map(projectId => {
                            const open = A.elem(eqId)(projectId)(openProjects)
                            return <Fragment key={projectId}>
                                <ProjectLine
                                    projectId={projectId}
                                    onUsersClick={setUsers}
                                    control={project => project.work_objects_count > 0 && <Button
                                        active={open}
                                        onClick={() => dispatch(projectListActions.openProjects.toggle(project.id))}
                                    >
                                        {open ? <FolderOpenIcon/> : <FolderIcon/>}
                                    </Button>}
                                />
                                {
                                    open && <ObjectList projectId={projectId}/>
                                }
                            </Fragment>
                        })
                    }
                </LoadingSkeleton>
            </InfiniteScroll>
            <AnimatePresence>
                {
                    isNonEmpty(users) && <SidePanel
                        onClose={() => setUsers([])}
                        className={styles.userContainer}
                        header={'Участники проекта'}
                    >
                        {
                            users.map(userRenderer)
                        }
                    </SidePanel>
                }
            </AnimatePresence>
        </statusContext.Provider>
    </div>
}

export default memo(ProjectList)
